<script lang="ts">
  import { fade } from "svelte/transition";
  import { motionDuration } from "~/lib/motion-duration";
  import { Image } from "@unpic/svelte/base";
  import { transform } from "unpic/providers/shopify";
  import ErrorState from "~/components/ui/ErrorState/ErrorState.svelte";
  import Price from "~/components/ui/Price/Price.svelte";
  import type { ProductCardProps } from "~/components/product/ProductCard/types";

  interface Props {
    data: ProductCardProps | Promise<ProductCardProps>;
    vertical?: boolean;
    onerror?: (error: unknown) => void;
    /** Set by a shell rendering this card inside a listbox — see SearchAutosuggestCore.items. */
    optionId?: string;
    selected?: boolean;
  }

  let { data, vertical = false, onerror: onDataError, optionId, selected }: Props = $props();

  // Rejection side effects live here, not in the {:catch} block: template
  // expressions may re-run on re-render. $derived keeps the effect from
  // re-running when a parent re-renders with the same promise.
  const pending = $derived(data);
  $effect(() => {
    const promise = pending;
    // {#await} accepts any thenable, so detect thenables rather than native Promises.
    if (typeof (promise as { then?: unknown } | null)?.then !== "function") return;
    let active = true;
    Promise.resolve(promise).catch((e: unknown) => {
      if (!active) return;
      console.error("[Aurora] ProductMiniCard failed to load product data:", e);
      try {
        onDataError?.(e);
      } catch (handlerError) {
        console.error("[Aurora] ProductMiniCard onerror handler threw:", handlerError);
      }
    });
    return () => {
      active = false;
    };
  });
</script>

{#await data}
  <article class="da-mini-card" class:da-mini-card--vertical={vertical}>
    <div class="da-mini-card__skeleton da-mini-card__skeleton--image" aria-hidden="true"></div>
    <div class="da-mini-card__body">
      <div class="da-mini-card__skeleton da-mini-card__skeleton--title" aria-hidden="true"></div>
      <div class="da-mini-card__skeleton da-mini-card__skeleton--price" aria-hidden="true"></div>
    </div>
  </article>
{:then d}
  <!-- A url-less card falls back to <article>, which cannot carry role="option"
       (aria-allowed-role). As a listbox option it becomes a plain <div> instead. -->
  <svelte:element
    this={d.url ? "a" : optionId ? "div" : "article"}
    href={d.url}
    role={optionId ? "option" : undefined}
    id={optionId}
    aria-selected={optionId ? selected : undefined}
    tabindex={optionId ? -1 : undefined}
    class="da-mini-card"
    class:da-mini-card--vertical={vertical}
  >
    <svelte:boundary>
      <div class="da-mini-card__image-container">
        {#if d.image}
          {#if vertical}
            <Image
              src={d.image}
              alt={d.imageAlt ?? ""}
              layout="constrained"
              width={350}
              height={350}
              breakpoints={[175, 350]}
              sizes="175px"
              operations={{ crop: "center" }}
              fallback="shopify"
              class="da-mini-card__image"
              transformer={transform}
              loading="lazy"
            />
          {:else}
            <Image
              src={d.image}
              alt={d.imageAlt ?? ""}
              layout="constrained"
              width={120}
              height={120}
              breakpoints={[60, 120]}
              sizes="60px"
              operations={{ crop: "center" }}
              fallback="shopify"
              class="da-mini-card__image"
              transformer={transform}
              loading="lazy"
            />
          {/if}
        {:else}
          <div class="da-mini-card__image-placeholder" aria-hidden="true"></div>
        {/if}
      </div>
      {#snippet failed()}
        <div class="da-mini-card__image-container"></div>
      {/snippet}
    </svelte:boundary>

    <div class="da-mini-card__body">
      <svelte:boundary
        onerror={(e) => console.error("[Aurora] ProductMiniCard [title] render failed:", e)}
      >
        <span class="da-mini-card__title">{d.title}</span>
        {#snippet failed()}{/snippet}
      </svelte:boundary>

      <svelte:boundary
        onerror={(e) => console.error("[Aurora] ProductMiniCard [price] render failed:", e)}
      >
        <div class="da-mini-card__price">
          <Price
            price={d.price}
            compareAtPrice={d.compareAtPrice}
            prefix={d.type === "product" && d.hasPriceRange ? "from " : undefined}
            saveBadge={false}
          />
        </div>
        {#snippet failed()}{/snippet}
      </svelte:boundary>
    </div>
  </svelte:element>
{:catch}
  <article
    class="da-mini-card"
    class:da-mini-card--vertical={vertical}
    transition:fade={{ duration: motionDuration(200) }}
  >
    <div class="da-mini-card__image-container" aria-hidden="true"></div>
    <div class="da-mini-card__body da-mini-card__body--error" role="status">
      <ErrorState message="Couldn't load product" />
    </div>
  </article>
{/await}

<style>
  @layer aurora {
    /* ── Base (shared) ──────────────────────────────────────────────────── */

    .da-mini-card {
      background: var(--da-color-white);
      border-radius: var(--da-radius-md);
      overflow: hidden;
      text-decoration: none;
      color: inherit;
    }

    .da-mini-card[aria-selected="true"] {
      outline: 2px solid var(--da-color-charcoal-500);
      outline-offset: 2px;
    }

    /* ── Horizontal / mobile ────────────────────────────────────────────── */

    .da-mini-card:not(.da-mini-card--vertical) {
      display: flex;
      flex-direction: row;
      align-items: center;
      min-height: 60px;
      box-shadow: var(--da-shadow-sm);
    }

    .da-mini-card:not(.da-mini-card--vertical) .da-mini-card__image-container {
      flex-shrink: 0;
      width: 60px;
      height: 60px;
    }

    .da-mini-card:not(.da-mini-card--vertical) .da-mini-card__body {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--da-space-3);
      padding: 8px 10px;
    }

    /* ── Vertical / desktop ─────────────────────────────────────────────── */

    .da-mini-card--vertical {
      display: flex;
      flex-direction: column;
      width: 175px;
      flex-shrink: 0;
      box-shadow: var(--da-shadow-md);
    }

    .da-mini-card--vertical .da-mini-card__image-container {
      width: 100%;
      aspect-ratio: 1;
    }

    .da-mini-card--vertical .da-mini-card__body {
      display: flex;
      flex-direction: column;
      gap: var(--da-space-2);
      padding: 8px 10px;
      width: 100%;
    }

    /* ── Shared internals ───────────────────────────────────────────────── */

    .da-mini-card__image-container {
      position: relative;
      overflow: hidden;
      background: var(--da-color-surface-subtle);
    }

    .da-mini-card__image-placeholder {
      width: 100%;
      height: 100%;
      background: var(--da-color-surface-subtle);
    }

    a.da-mini-card:hover .da-mini-card__title {
      text-decoration: underline;
    }

    .da-mini-card__title {
      display: block;
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-xs);
      line-height: var(--da-line-height-xs);
      font-weight: var(--da-font-weight-semibold);
      color: var(--da-color-text-primary);
      overflow: hidden;
    }

    .da-mini-card__price {
      flex-shrink: 0;
      width: 71px;
      text-align: right;
      margin-inline-start: auto;
      --da-price-font-size: var(--da-font-size-sm);
      --da-price-line-height: var(--da-line-height-sm);
      --da-price-compare-font-size: var(--da-font-size-xs);
      --da-price-compare-line-height: var(--da-line-height-xs);
    }

    /* Horizontal (mobile) cards: Price renders display:flex, so text-align has no effect.
       Explicitly right-align the inner flex container. */
    .da-mini-card:not(.da-mini-card--vertical) .da-mini-card__price :global(.da-price) {
      justify-content: flex-end;
    }

    .da-mini-card--vertical .da-mini-card__price {
      width: auto;
      text-align: left;
      margin-inline-start: 0;
    }

    /* ── Error state ───────────────────────────────────────────────────── */

    .da-mini-card:not(.da-mini-card--vertical) .da-mini-card__body--error,
    .da-mini-card--vertical .da-mini-card__body--error {
      align-items: center;
      justify-content: center;
    }

    /* ── Skeleton states ────────────────────────────────────────────────── */

    .da-mini-card__skeleton {
      background: var(--da-color-surface-subtle);
      border-radius: var(--da-radius-sm);
      animation: da-skeleton-pulse 1.5s ease-in-out infinite;
    }

    .da-mini-card:not(.da-mini-card--vertical) .da-mini-card__skeleton--image {
      flex-shrink: 0;
      width: 60px;
      height: 60px;
    }

    .da-mini-card--vertical .da-mini-card__skeleton--image {
      width: 100%;
      aspect-ratio: 1;
      height: auto;
    }

    .da-mini-card__skeleton--title {
      height: 1rem;
      width: 120px;
      flex: 1;
    }

    /* Vertical titles wrap to two lines and the price sits below them, so the
       placeholder has to be the same two lines tall or the card is short and the
       panel reflows when results land. */
    .da-mini-card--vertical .da-mini-card__skeleton--title {
      height: calc(var(--da-line-height-xs) * 2);
      width: 100%;
      flex: none;
    }

    .da-mini-card__skeleton--price {
      height: 1rem;
      width: 60px;
      flex-shrink: 0;
      animation-delay: 0.15s;
    }

    .da-mini-card--vertical .da-mini-card__skeleton--price {
      height: var(--da-line-height-sm);
    }

    @keyframes da-skeleton-pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }
  }
</style>
